import React from "react";

import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
} from "@mui/material";
import { Link, Trash01Icon, Typography, User03Icon } from "components";
import { Paths, Permissions } from "configs";
import { error } from "configs/colors";
import usePermission from "hooks/usePermission";
import { DeptManagers } from "services";
import { i18n } from "translations";

interface Props {
  item: DeptManagers;
  handleClickTrash: () => void;
}

interface UserDetailProps {
  title: string;
  fullName: string;
  email: string;
  id: number;
}

const UserDetail = (props: UserDetailProps) => {
  const { title = "", fullName = "", id, email } = props;
  return (
    <List
      sx={{
        width: "100%",
        maxWidth: 360,
        bgcolor: "background.paper",
        padding: 0,
      }}
    >
      <ListItem sx={{ padding: "8px" }} alignItems="center">
        <ListItemAvatar>
          <Avatar alt="avatar">{fullName[0]}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Link to={`${Paths.EmployeesDetail}/${id}`}>
              <Typography type="TextSM">{fullName}</Typography>
            </Link>
          }
          secondary={
            <Typography type="TextSM">
              <strong>{title}</strong> <br /> <i>{email}</i>
            </Typography>
          }
        />
      </ListItem>
    </List>
  );
};

const Manager = (props: Props) => {
  const { handleClickTrash, item } = props;
  const { canAccess } = usePermission();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const idPopover = open ? "simple-popover" : undefined;

  return (
    <Stack
      key={item.employee.id}
      direction="row"
      alignItems="center"
      columnGap="5px"
      sx={{
        position: "relative",
        cursor: "pointer",
        "& .remove-icon": {
          display: "none",
        },
        "&:hover": {
          "& .remove-icon": {
            display: "block",
          },
        },
      }}
    >
      <User03Icon sx={{ fontSize: "10px" }} />
      <Typography
        variant="caption"
        noWrap
        overflow="visible"
        sx={{ "& a": { color: "inherit" } }}
        onClick={handleClick}
      >
        <i>{item.employee.fullName}</i>
      </Typography>
      <Stack sx={{ width: "16px", height: "16px" }} justifyContent="center">
        {canAccess(Permissions.OrgChart.RemoveManager) && (
          <Tooltip
            title={i18n.t("deleteManager")}
            arrow
            placement="right-start"
          >
            <Stack className="remove-icon">
              <Trash01Icon
                onClick={handleClickTrash}
                sx={{ fontSize: "15px", color: error[500] }}
              />
            </Stack>
          </Tooltip>
        )}
      </Stack>
      <Popover
        id={idPopover}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <UserDetail
          fullName={item.employee.fullName}
          title={item.role || ""}
          id={item.employee.id}
          email={item.employee.email}
        />
      </Popover>
    </Stack>
  );
};

export default Manager;
